import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  STOREFRONT_DEFAULT,
  applyBrandTokens,
  resolveBrand,
  type ResolvedBrand,
} from '@mlm/shared';
import { useAuthStore } from '../../lib/auth-store';
import { usePricingSlotId } from '../../lib/hooks/use-my-slots';
import { useCartStore } from './cart-store';
import {
  getTenantConfig,
  listPublicProducts,
  getStorefront,
  getPublicProduct,
  getStorefrontProduct,
  getFlashSale,
  getLandingPackages,
  getFeaturedPackages,
  getStorefrontContent,
  getCategoryCounts,
  getPopularTags,
  getMerchantStats,
  listMerchantTransactions,
  listMerchantReviews,
  listMerchantTopSelling,
  getMerchantFollowState,
  followMerchant,
  unfollowMerchant,
  getOfficialStoreFollowState,
  getOfficialStoreStats,
  followOfficialStore,
  unfollowOfficialStore,
  listCategories,
  listMerchantFeaturedReviews,
  listOfficialStoreFeaturedReviews,
  listProductReviews,
  listProductTestimonials,
  productImageUrl,
  searchShops,
  type PublicProduct,
  type StorefrontProduct,
  type StorefrontResponse,
} from '../../lib/api';
import { getStoredRef } from '../../lib/ref';
import { productHref, type ProductHrefInput } from './product-href';
import type { DisplayProduct } from './types';

type StorefrontContext = StorefrontResponse['context'];

/** Stable pseudo-random int from a string — for the decorative rating only. */
function hashInt(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Map a real API product to the card's display shape. */
export function toDisplay(p: PublicProduct | StorefrontProduct): DisplayProduct {
  const isSf = 'priceEffective' in p;
  const price = Number(isSf ? (p as StorefrontProduct).priceEffective : p.price);
  const base = isSf ? Number((p as StorefrontProduct).priceBase) : price;
  const off = isSf ? Math.round(Number((p as StorefrontProduct).appliedDiscountPct)) : 0;
  const h = hashInt(p.id);
  return {
    id: p.id,
    name: p.name,
    price,
    was: base > price ? base : 0,
    off,
    pv: Number(p.pv) || 0,
    // Flat GC/coin sticker prices — only the member-priced (storefront) path
    // exposes them; the public catalog is cash-only, so leave them null.
    priceGc: isSf ? numOrNull((p as StorefrontProduct).priceGc) : null,
    priceCoin: isSf ? numOrNull((p as StorefrontProduct).priceCoin) : null,
    // Real stock only flows through the member-priced (storefront) path; the
    // public catalog has no per-buyer inventory context, so leave it undefined.
    stockQty: isSf ? (p as StorefrontProduct).stockQty : undefined,
    assembledFromComponents: isSf ? (p as StorefrontProduct).assembledFromComponents : undefined,
    categoryId: p.categoryId ?? null,
    rating: 5 - (h % 2),
    image: p.images?.length ? productImageUrl(p.id, 0) : null,
    freeShipping: isSf ? (p as StorefrontProduct).freeShipping : undefined,
    isVirtual: p.isVirtual,
    description: p.description ?? null,
    replicatedPageSlug: p.replicatedPageSlug ?? null,
    cashback: isSf ? Number((p as StorefrontProduct).cashback) || 0 : 0,
    voucherBadge: isSf ? (p as StorefrontProduct).voucherBadge?.label : undefined,
    featured: p.featured,
    sellerId: p.merchantUserId ?? '',
    // Resolved to the tenant's brand at render time via `displaySellerName` (this
    // pure mapper has no brand context). Empty = derive from sellerId.
    sellerName: '',
    variants: (isSf ? (p as StorefrontProduct).variants ?? [] : []).map((v) => ({
      id: v.id,
      name: v.name,
      attributes: v.attributes,
      price: Number(v.price),
      was: v.srp ? Number(v.srp) : 0,
      priceGc: numOrNull(v.priceGc),
      priceCoin: numOrNull(v.priceCoin),
      inStock: v.inStock,
      stockQty: v.stockQty,
    })),
  };
}

/** Parse a nullable money string to a number, or null when absent. */
function numOrNull(v: string | null | undefined): number | null {
  return v == null ? null : Number(v);
}

export function useTenantConfig() {
  return useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig, retry: 0 });
}

/**
 * Whether the shop + member portal exist on this tenant at all.
 *
 * False only in `public_site_info_only` mode, where the public site is a plain
 * information site: the bespoke landing pages, and nothing else. Every commerce
 * and auth affordance (cart, search, category nav, login, sign-up, account) is
 * hidden, and `App` doesn't even mount those routes.
 *
 * Defaults to TRUE while the config query is in flight or has failed — the
 * storefront is the norm, and a fetch blip must never blank a working shop.
 * `App`'s boot gate blocks the first paint until the config resolves, so an
 * info-only tenant never flashes commerce chrome.
 *
 * An IMPERSONATED session is always commerce-on, and must stay in lockstep with
 * the matching carve-out in `App`: impersonation is the super-admin escape hatch
 * onto an info-only box, so it mounts the full route tree — chrome that hid the
 * cart while /cart was reachable would just be confusing.
 */
export function useCommerceEnabled(): boolean {
  const infoOnly = useTenantConfig().data?.publicSiteInfoOnly ?? false;
  const impersonating = !!useAuthStore((s) => s.user?.impersonator);
  return !infoOnly || impersonating;
}

/**
 * Where a product card links. ONE call site for the
 * `virtual_products_open_replicated_page` rule, so every grid — the shared
 * ProductCard, the flash-sale strip, the featured-packages row, and each
 * bespoke tenant landing — stays in agreement. See `productHref`.
 */
export function useProductHref(): (p: ProductHrefInput) => string {
  const openReplicatedPage = useTenantConfig().data?.virtualProductsOpenReplicatedPage ?? false;
  // Read once per render, not per card: sessionStorage access in a hot map()
  // is needless, and the ref cannot change mid-render.
  const storedRef = getStoredRef();
  return useMemo(
    () => (p: ProductHrefInput) => productHref(p, { openReplicatedPage, storedRef }),
    [openReplicatedPage, storedRef],
  );
}

// Legacy hardcoded seller names that may be baked into persisted carts / older
// payloads — mapped to the tenant's resolved brand so old localStorage carts
// and any stale snapshots re-brand on render instead of showing "Omni".
const LEGACY_HOUSE_NAMES = new Set(['Omni Official Store', 'Omni Wellness Official']);
const LEGACY_SELLER_NAMES = new Set(['Omni Marketplace Seller']);

/**
 * Resolve a product/cart line's seller display name against the tenant brand.
 * House lines (empty sellerId) → the brand's house store name; merchant lines
 * show their real business name, or the generic marketplace-seller label when we
 * only have a legacy/blank placeholder.
 */
export function displaySellerName(
  sellerId: string,
  sellerName: string | undefined,
  brand: ResolvedBrand,
): string {
  const name = (sellerName ?? '').trim();
  if (!sellerId || LEGACY_HOUSE_NAMES.has(name)) return brand.houseStoreName;
  if (!name || LEGACY_SELLER_NAMES.has(name)) return brand.marketplaceSellerName;
  return name;
}

function useRawStorefrontContent() {
  return useQuery({
    queryKey: ['storefront-content'],
    queryFn: getStorefrontContent,
    initialData: STOREFRONT_DEFAULT,
    // Always treat CMS content as stale so a reload / navigation / tab-focus
    // immediately reflects an admin save (the admin SPA can't invalidate this
    // SPA's cache cross-origin). `initialData` still prevents an empty flash.
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 0,
  });
}

/**
 * The tenant's resolved brand terms (brand name, house store name, mall badge,
 * wallet label, …). Combines the raw storefront `brand` block + footer with the
 * tenant-config `name` (seeded by the build-time brand so a deployed tenant
 * never flashes the neutral fallback). See `resolveBrand` in @mlm/shared.
 */
export function useBrand(): ResolvedBrand {
  const tenantName = useTenantConfig().data?.name ?? import.meta.env.VITE_BRAND ?? null;
  const raw = useRawStorefrontContent().data;
  return useMemo(() => resolveBrand(raw, tenantName), [raw, tenantName]);
}

/**
 * Keep the browser-tab title in sync with the tenant's resolved site title. The
 * build-time bake (or the static index.html <title>) covers the first paint; this
 * updates it once the tenant's resolved title differs (e.g. a stale bake, or an
 * admin-edited title). Mount once at the app root.
 *
 * Reads `siteTitle`, NOT `brandName` — a tenant's tab title is often its full
 * name + tagline, while `brandName` stays short for the logo / badge / wallet.
 * `siteTitle` falls back to `brandName`, so tenants that never set one are
 * unaffected.
 */
export function useBrandDocumentTitle() {
  const siteTitle = useBrand().siteTitle;
  useEffect(() => {
    if (siteTitle && document.title !== siteTitle) document.title = siteTitle;
  }, [siteTitle]);
}

/**
 * Catalog: member-priced when signed in (real discount %/PV), public otherwise.
 * Real API data only — no mock fallback. Empty array until the catalog loads or
 * if the tenant has no active products yet.
 */
export function useCatalog(search?: string, seller?: string) {
  const authed = !!useAuthStore((s) => s.token);
  const term = search?.trim() ?? '';
  // The SELECTED slot must be part of the key, not just the request: switching
  // slots has to re-price the grid, and the switcher deliberately doesn't
  // invalidate — it relies on every price-bearing key carrying the slot.
  const slotId = usePricingSlotId();
  const q = useQuery({
    queryKey: ['catalog', authed, term, seller ?? null, slotId],
    queryFn: async (): Promise<{ products: DisplayProduct[]; context: StorefrontContext | null }> => {
      if (authed) {
        const { products, context } = await getStorefront(term || undefined, seller, slotId);
        return { products: products.map(toDisplay), context };
      }
      const rows = await listPublicProducts(term || undefined, seller);
      return { products: rows.map(toDisplay), context: null };
    },
    // Keep the current results visible while a new search refetches so the grid
    // doesn't flash empty on each keystroke (debounced upstream).
    placeholderData: (prev) => prev,
    retry: 0,
  });
  return {
    products: q.data?.products ?? [],
    context: q.data?.context ?? null,
    isLoading: q.isLoading,
    query: q,
  };
}

/**
 * Refresh cart line prices to the buyer's effective (discounted) price whenever
 * they become authenticated — no matter HOW they logged in (checkout dialog,
 * another tab, session restore). Without this, prices snapshotted while logged
 * out (SRP) would persist into checkout even though the server charges the
 * discounted price. Mount this on the price-display surfaces (cart, checkout).
 */
export function useSyncCartPricesOnLogin() {
  const authed = !!useAuthStore((s) => s.token);
  const syncCartPrices = useCartStore((s) => s.syncCartPrices);
  // KEEP IN SYNC with checkout.tsx `isDropship` — a stored dropship ref only
  // forces SRP display when the tenant actually posts dropship orders. The ref
  // survives in localStorage until an order is placed, so gating on it alone
  // showed SRP on tenants that charge the buyer's discounted price.
  const dropshipActive = useTenantConfig().data?.dropshippingIncome ?? false;
  // This effect has no query key, so the dep array IS the key: `slotId` belongs
  // in it so switching slots re-snapshots the cart at the new slot's prices.
  // Without it the cart keeps the price of whichever slot was active at login.
  const slotId = usePricingSlotId();
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    getStorefront(undefined, undefined, slotId)
      .then(({ products }) => {
        if (!cancelled) syncCartPrices(products, { dropshipActive });
      })
      .catch(() => {
        /* leave snapshots in place if the priced catalog can't load */
      });
    return () => {
      cancelled = true;
    };
  }, [authed, slotId, syncCartPrices, dropshipActive]);
}

/** Storefront flash-sale strip: admin-configured products + optional countdown. */
export function useFlashSale() {
  const q = useQuery({
    queryKey: ['flash-sale'],
    queryFn: getFlashSale,
    retry: 0,
  });
  const products = (q.data?.products ?? []).map(toDisplay);
  return {
    enabled: !!q.data?.enabled && products.length > 0,
    endsAt: q.data?.endsAt ?? null,
    products,
    isLoading: q.isLoading,
  };
}

/** Landing "packages": admin-curated, ORDERED products for a tenant landing's packages section. */
export function useLandingPackages() {
  const q = useQuery({
    queryKey: ['landing-packages'],
    queryFn: getLandingPackages,
    retry: 0,
  });
  return {
    products: (q.data?.products ?? []).map(toDisplay),
    isLoading: q.isLoading,
  };
}

/** Storefront "Featured Packages": admin-curated, ORDERED products + optional heading. */
export function useFeaturedPackages() {
  const q = useQuery({
    queryKey: ['featured-packages'],
    queryFn: getFeaturedPackages,
    retry: 0,
  });
  const products = (q.data?.products ?? []).map(toDisplay);
  return {
    enabled: !!q.data?.enabled && products.length > 0,
    heading: q.data?.heading ?? null,
    products,
    isLoading: q.isLoading,
  };
}

export function useCategories() {
  return useQuery({ queryKey: ['categories'], queryFn: listCategories, retry: 0 });
}

/**
 * Storefront CMS content (hero, banners, vouchers, collections, store stats,
 * trust/assurance badges, header links, footer, copy). Seeded with the shared
 * `STOREFRONT_DEFAULT` so first paint is identical to the old hardcoded chrome
 * and never flashes empty while the network request is in flight.
 */
export function useStorefrontContent() {
  const tenantName = useTenantConfig().data?.name ?? import.meta.env.VITE_BRAND ?? null;
  const query = useRawStorefrontContent();
  const raw = query.data;
  // Expand brand tokens ({brand}/{company}/{wallet}/{year}) across all copy so
  // every existing consumer (footer, banners, info pages, page titles) is
  // branded in one place — and a raw token can never render, even from the
  // token-bearing `initialData`.
  const data = useMemo(() => applyBrandTokens(raw, resolveBrand(raw, tenantName)), [raw, tenantName]);
  return { ...query, data };
}

/** Per-category product counts (matches the catalog grid). Pass `seller='house'`
 * on the Official Store so badges count house products only. */
export function useCategoryCounts(seller?: 'house') {
  return useQuery({
    queryKey: ['category-counts', seller ?? 'all'],
    queryFn: () => getCategoryCounts(seller),
    staleTime: 60_000,
    retry: 0,
  });
}

/** Popular search tags — the admin-curated `searchSuggestions` when set,
 * otherwise auto-derived from real sales (server decides). Seeded with the CMS
 * default so it never flashes empty before the request resolves. Treated as
 * always-stale (like `useStorefrontContent`) so an admin edit to the suggestions
 * reflects on the next navigation / tab-focus instead of lagging a stale window. */
export function usePopularTags() {
  return useQuery({
    queryKey: ['popular-tags'],
    queryFn: getPopularTags,
    initialData: STOREFRONT_DEFAULT.header.searchSuggestions,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 0,
  });
}

/** Shops whose name matches the search term — the "Shops related to …" band. */
export function useShopSearch(search: string) {
  const term = search.trim();
  return useQuery({
    queryKey: ['shop-search', term],
    queryFn: () => searchShops(term, 3),
    enabled: term.length > 0,
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

/** Public merchant stats (Products / Rating / Joined / Followers / Following). */
export function useMerchantStats(merchantId: string | undefined) {
  return useQuery({
    queryKey: ['merchant-stats', merchantId],
    queryFn: () => getMerchantStats(merchantId!),
    enabled: !!merchantId,
    retry: 0,
  });
}

/** The merchant's completed transactions (reference numbers) — Total Transactions drill-down. */
export function useMerchantTransactions(merchantId: string | undefined) {
  return useQuery({
    queryKey: ['merchant-transactions', merchantId],
    queryFn: () => listMerchantTransactions(merchantId!),
    enabled: !!merchantId,
    retry: 0,
  });
}

/** Reviews across the merchant's products (5★ → 1★) — Rating drill-down. */
export function useMerchantReviews(merchantId: string | undefined, rating?: number) {
  return useQuery({
    queryKey: ['merchant-reviews', merchantId, rating ?? null],
    queryFn: () => listMerchantReviews(merchantId!, { rating }),
    enabled: !!merchantId,
    retry: 0,
    placeholderData: (prev) => prev,
  });
}

/** Reviews the merchant pinned as "Featured Feedback" on their shop. */
export function useMerchantFeaturedReviews(merchantId: string | undefined) {
  return useQuery({
    queryKey: ['merchant-featured-reviews', merchantId],
    queryFn: () => listMerchantFeaturedReviews(merchantId!),
    enabled: !!merchantId,
    retry: 0,
  });
}

/** Reviews admin pinned as "Featured Feedback" on the Official Store. */
export function useOfficialStoreFeaturedReviews() {
  return useQuery({
    queryKey: ['official-store-featured-reviews'],
    queryFn: () => listOfficialStoreFeaturedReviews(),
    retry: 0,
  });
}

/** Top-selling products (by units sold) for the merchant shop's Top Selling row. */
export function useMerchantTopSelling(merchantId: string | undefined) {
  return useQuery({
    queryKey: ['merchant-top-selling', merchantId],
    queryFn: () => listMerchantTopSelling(merchantId!),
    enabled: !!merchantId,
    retry: 0,
  });
}

/**
 * Follow state + toggle for a merchant. Follow state is only fetched when the
 * buyer is signed in (the endpoint requires auth); the mutation refreshes both
 * the follow state and the merchant's follower count.
 */
export function useMerchantFollow(merchantId: string | undefined) {
  const authed = !!useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const stateQ = useQuery({
    queryKey: ['merchant-follow', merchantId],
    queryFn: () => getMerchantFollowState(merchantId!),
    enabled: !!merchantId && authed,
    retry: 0,
  });
  const toggle = useMutation({
    mutationFn: () =>
      stateQ.data?.following ? unfollowMerchant(merchantId!) : followMerchant(merchantId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-follow', merchantId] });
      qc.invalidateQueries({ queryKey: ['merchant-stats', merchantId] });
    },
  });
  return { following: !!stateQ.data?.following, canFollow: authed, toggle };
}

/**
 * Follow state + toggle + live follower count for the in-house Omni Official
 * Store. Mirrors `useMerchantFollow` but targets the single house store (no id).
 */
export function useOfficialStoreFollow() {
  const authed = !!useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const stateQ = useQuery({
    queryKey: ['official-store-follow'],
    queryFn: getOfficialStoreFollowState,
    enabled: authed,
    retry: 0,
  });
  const toggle = useMutation({
    mutationFn: () =>
      stateQ.data?.following ? unfollowOfficialStore() : followOfficialStore(),
    onSuccess: (data) => {
      qc.setQueryData(['official-store-follow'], data);
    },
  });
  return {
    following: !!stateQ.data?.following,
    followers: stateQ.data?.followers ?? null,
    canFollow: authed,
    toggle,
  };
}

/** Live Official Store stats (Products / Rating / Followers) — public. */
export function useOfficialStoreStats() {
  return useQuery({
    queryKey: ['official-store-stats'],
    queryFn: getOfficialStoreStats,
    staleTime: 60_000,
    retry: 0,
  });
}

/** Single product detail (member-priced when signed in). */
export function useProductDetail(id: string | undefined) {
  const authed = !!useAuthStore((s) => s.token);
  const slotId = usePricingSlotId();
  return useQuery({
    queryKey: ['product', id, authed, slotId],
    queryFn: async (): Promise<
      DisplayProduct & {
        sku: string;
        description: string | null;
        detailsHtml: string | null;
        videoUrl: string | null;
        images: string[];
        categoryId: string | null;
        weightKg: string;
        lengthCm: string;
        widthCm: string;
        heightCm: string;
      }
    > => {
      const raw = authed
        ? (await getStorefrontProduct(id!, slotId)).product
        : await getPublicProduct(id!);
      return {
        ...toDisplay(raw),
        sku: raw.sku,
        description: raw.description,
        detailsHtml: raw.detailsHtml ?? null,
        videoUrl: raw.videoUrl ?? null,
        images: raw.images ?? [],
        categoryId: raw.categoryId ?? null,
        weightKg: raw.weightKg ?? '0',
        lengthCm: raw.lengthCm ?? '0',
        widthCm: raw.widthCm ?? '0',
        heightCm: raw.heightCm ?? '0',
      };
    },
    enabled: !!id,
    retry: 0,
  });
}

export function useProductReviews(
  id: string | undefined,
  filter?: { rating?: number; hasPhoto?: boolean; hasComment?: boolean },
) {
  return useQuery({
    queryKey: ['reviews', id, filter ?? null],
    queryFn: () => listProductReviews(id!, filter),
    enabled: !!id,
    retry: 0,
    // Keep the previous list visible while a filter refetch is in flight so the
    // chips don't flash empty.
    placeholderData: (prev) => prev,
  });
}

export function useProductTestimonials(id: string | undefined) {
  return useQuery({
    queryKey: ['testimonials', id],
    queryFn: () => listProductTestimonials(id!),
    enabled: !!id,
    retry: 0,
  });
}
